import {Filter, type FilterQuery, SubmitEvent} from '@github-ui/filter'
import {
  type AnyBlock,
  type FilterConfig,
  getLastFilterBlockValue,
  type MutableFilterBlock,
} from '@github-ui/filter-core'
import {KeyOnlyFilterProvider} from '@github-ui/filter-core/providers'
import {useSyncedState} from '@github-ui/use-synced-state'
import {GitBranchIcon, KeyIcon} from '@primer/octicons-react'
import {useCallback, useId, useMemo} from 'react'
import {WorkflowRunsAsyncFilterProvider} from '../workflow-runs/workflow-runs-filter-providers'

const singleValueOptions = {
  filterTypes: {valueless: false, hasValue: false, multiValue: false, multiKey: false, exclusive: false},
} as const

export function CachesFilter({
  branchesPath,
  query,
  onQueryChange,
  track,
}: {
  branchesPath: string
  query: string
  onQueryChange: (query: string) => void
  track: (action: string) => void
}) {
  const id = useId()
  const [filterValue, setFilterValue] = useSyncedState(query)
  const providers = useMemo(
    () => [
      new KeyOnlyFilterProvider(
        {key: 'key', displayName: 'Key', description: 'Filter by cache key', icon: KeyIcon, priority: 1},
        {...singleValueOptions, caseSensitiveValues: true, priority: 1},
      ),
      new CacheBranchSuggestionsFilterProvider(
        {key: 'branch', displayName: 'Branch', description: 'Filter by branch', icon: GitBranchIcon, priority: 2},
        branchesPath,
        {...singleValueOptions, caseSensitiveValues: true, priority: 2},
      ),
    ],
    [branchesPath],
  )
  const handleChange = useCallback(
    (value: string) => {
      setFilterValue(value)
      if (!value.trim() && query) {
        track('clear_search')
        onQueryChange('')
      }
    },
    [onQueryChange, query, setFilterValue, track],
  )
  const handleSubmit = useCallback(
    (filterQuery: FilterQuery, eventType: SubmitEvent) => {
      if (eventType === SubmitEvent.Clear) return
      track('search')
      onQueryChange(filterQuery.raw)
    },
    [onQueryChange, track],
  )

  return (
    <Filter
      id={id}
      label="Filter caches"
      placeholder="Filter caches"
      variant="input"
      providers={providers}
      filterValue={filterValue}
      onChange={handleChange}
      onSubmit={handleSubmit}
      settings={{groupAndKeywordSupport: false, showSuggestionsOnFocus: true}}
    />
  )
}

class CacheBranchSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<string> {
  override async fetchSuggestions(_value: string) {
    // The cache endpoint returns every branch, so fetch once and filter locally.
    const branches = await super.fetchSuggestions('')
    return branches?.every(branch => typeof branch === 'string') ? [...new Set(branches)] : null
  }

  override async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    const suggestions = await super.getSuggestions(filterQuery, filterBlock, config, caretIndex)
    const value = getLastFilterBlockValue(filterBlock, caretIndex).toLowerCase()
    return suggestions?.filter(suggestion => suggestion.displayName?.toLowerCase().includes(value)) ?? null
  }

  protected toSuggestion(branch: string) {
    return {value: branch, displayName: branch}
  }
}
